<script>
import { storeToRefs } from 'pinia'
import {
  formButton,
  formInput,
  formSelect,
  formMoney,
  greatherThan,
  isValidCPF
} from 'tb-design_system-web'
import { useForm } from 'vee-validate'
import { onMounted, ref, provide, watch, computed } from 'vue'
import { useMeta } from 'vue-meta'
import { useRoute, useRouter } from 'vue-router'
import * as yup from 'yup'

import RegisterLayout from '@/components/shared/registerLayout.vue'
import StepProgressBar from '@/components/shared/stepProgressBar.vue'
import { moneyFormat } from '@/composables/utils/moneyConfig'
import { stateOptions, useCityOptions } from '@/composables/utils/useBrazilianStates'
import { getBanks, fetchCachedFinishData, getCountriesBanks } from '@/services/api'
import { handleGlobalError } from '@/services/errorHandler'
import { useCache } from '@/stores/cache'
import { useOnboardingStore } from '@/stores/onboarding'

export default {
  name: 'RegisterFinishPFStep1',
  components: {
    RegisterLayout,
    StepProgressBar,
    formInput,
    formMoney,
    formSelect,
    formButton
  },
  setup() {
    useMeta({ title: 'Cadastro PF - Etapa 1/3' })

    const route = useRoute()
    const router = useRouter()

    const onboardingStore = useOnboardingStore()
    const { pfForm } = storeToRefs(onboardingStore)
    const cacheStore = useCache()

    const userCpf = ref(pfForm.value.documentId)

    const loading = ref(false)

    const cityOfBirthValue = ref('')
    const stateOfBirthValue = ref('')
    const cityOptions = useCityOptions(stateOfBirthValue)
    const professionValue = ref('')
    const pfCompanyNameValue = ref('')
    const averageMonthlyIncomeValue = ref('')
    const totalAssetsValue = ref('')
    const isPepValue = ref(null)
    const pepNameValue = ref('')
    const pepCpfValue = ref('')
    const hasOtherCitizenshipValue = ref(null)
    const otherCitizenshipValue = ref('')
    const isEuaResidentialTaxCitizenValue = ref(null)

    const alreadySubmittedOnce = ref(false)
    provide('alreadySubmittedOnce', alreadySubmittedOnce)

    watch(isPepValue, (val) => {
      setFieldValue('isPep', val)
    })

    watch(hasOtherCitizenshipValue, (val) => {
      setFieldValue('hasOtherCitizenship', val)
    })

    watch(isEuaResidentialTaxCitizenValue, (val) => {
      setFieldValue('isEuaResidentialTaxCitizen', val)
    })

    const { handleSubmit, errors, setFieldValue } = useForm({
      validationSchema: yup.object({
        cityOfBirth: yup.string().required(),
        stateOfBirth: yup.string().required(),
        profession: yup.string().required(),
        pfCompanyName: yup.string().required(),
        averageMonthlyIncome: greatherThan(yup),
        totalAssets: greatherThan(yup),
        isPep: yup.boolean().required('Campo obrigatório'),
        pepName: yup.string().when('isPep', {
          is: true,
          then: (schema) => schema.required('Campo obrigatório')
        }),
        pepCpf: yup.string().when('isPep', {
          is: true,
          then: (schema) => schema.required('Campo obrigatório').test((value) => isValidCPF(value))
        }),
        hasOtherCitizenship: yup.boolean().required('Campo obrigatório'),
        otherCitizenship: yup.string().when('hasOtherCitizenship', {
          is: true,
          then: (schema) => schema.required('Campo obrigatório')
        }),
        isEuaResidentialTaxCitizen: yup.boolean().nullable().required('Selecione uma opção')
      }),
      initialValues: {
        isPep: isPepValue.value,
        hasOtherCitizenship: hasOtherCitizenshipValue.value,
        isEuaResidentialTaxCitizen: isEuaResidentialTaxCitizenValue.value
      },
      context: {
        userCpf: pfForm.value.documentId
      }
    })

    onMounted(async () => {
      const token = route.query.token
      if (token == null) router.replace('/')

      onboardingStore.setToken(token)
      onboardingStore.setTypeClient('PF')

      try {
        fetchBanks()
        fetchCountriesBanks()
        const { data: response } = await fetchCachedFinishData(token)
        cacheStore.setFinishCache(response.payload)

        const pfData = response.payload || {}
        onboardingStore.documentId = pfData.documentId
        onboardingStore.clientId = pfData.clientId
        onboardingStore.clientName = pfData.clientName
        if (pfData.pf.cityOfBirth === '' && pfData.pf.stateOfBirth === '') {
          onboardingStore.setPFForm({ documents: pfData.documents })
          return
        }
        onboardingStore.setPFForm(pfData)

        const fieldMap = {
          cityOfBirthValue: 'cityOfBirth',
          stateOfBirthValue: 'stateOfBirth',
          professionValue: 'profession',
          pfCompanyNameValue: 'pfCompanyName',
          averageMonthlyIncomeValue: 'averageMonthlyIncome',
          totalAssetsValue: 'totalAssets',
          isPepValue: 'isPep',
          pepNameValue: 'pepName',
          pepCpfValue: 'pepCpf',
          hasOtherCitizenshipValue: 'hasOtherCitizenship',
          otherCitizenshipValue: 'otherCitizenship',
          isEuaResidentialTaxCitizenValue: 'isEuaResidentialTaxCitizen'
        }

        Object.entries(fieldMap).forEach(([refName, storeKey]) => {
          eval(refName).value = pfForm.value[storeKey]
        })
        hasOtherCitizenshipValue.value = pfForm.value.hasOtherCitizenship == null ? false : true
        isEuaResidentialTaxCitizenValue.value =
          pfForm.value.isEuaResidentialTaxCitizen == null ? false : true
      } catch (err) {
        router.push('/')
        handleGlobalError(err, 'Erro ao buscar dados')
      }
    })

    const fetchBanks = async () => {
      try {
        const response = await getBanks()
        const formattedBanks = response.data.payload.map((item) => ({
          key: item.id,
          name: `${item.id} - ${item.name}`
        }))
        cacheStore.setBankCache(formattedBanks)
      } catch (err) {
        handleGlobalError(err)
      }
    }

    const getCountryOptions = (items = []) => {
      return [...new Set(items.map((item) => item.country).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }))
        .map((country) => ({
          key: country,
          name: country
        }))
    }

    const fetchCountriesBanks = async () => {
      try {
        const response = await getCountriesBanks()
        cacheStore.countries = [...getCountryOptions(response.data.payload)]
      } catch (err) {
        handleGlobalError(err)
      }
    }

    const onSubmitAction = () => {
      alreadySubmittedOnce.value = true
      return onSubmit()
    }

    const onSubmit = handleSubmit(async () => {
      loading.value = true

      try {
        onboardingStore.setStepData(1, {
          cityOfBirth: cityOfBirthValue.value,
          stateOfBirth: stateOfBirthValue.value,
          profession: professionValue.value,
          pfCompanyName: pfCompanyNameValue.value,
          averageMonthlyIncome: averageMonthlyIncomeValue.value,
          totalAssets: totalAssetsValue.value,
          isPep: isPepValue.value,
          pepName: pepNameValue.value,
          pepCpf: pepCpfValue.value,
          hasOtherCitizenship: hasOtherCitizenshipValue.value,
          otherCitizenship: otherCitizenshipValue.value,
          isEuaResidentialTaxCitizen: isEuaResidentialTaxCitizenValue.value
        })
        router.push('/finalizar-cadastro/banco')
      } catch (err) {
        handleGlobalError(err)
      } finally {
        loading.value = false
      }
    })

    const isIbgeAPIAvailable = computed(() => cityOptions.value != null)

    return {
      loading,
      onSubmitAction,

      moneyFormat,

      cityOfBirthValue,
      stateOfBirthValue,
      professionValue,
      pfCompanyNameValue,
      averageMonthlyIncomeValue,
      totalAssetsValue,
      isPepValue,
      pepNameValue,
      pepCpfValue,
      hasOtherCitizenshipValue,
      otherCitizenshipValue,
      isEuaResidentialTaxCitizenValue,
      errors,
      alreadySubmittedOnce,

      stateOptions,
      cityOptions,
      isIbgeAPIAvailable
    }
  }
}
</script>

<template>
  <RegisterLayout>
    <div class="col-12 col-md-9 h-100 onboarding pb-5">
      <div class="row">
        <StepProgressBar
          class="mt-5 mb-5"
          :steps="['Dados Pessoais', 'Financeiro', 'Anexos']"
          :current="1"
        />
      </div>
      <div class="row d-flex justify-content-center mt-4">
        <div class="col-12 col-md-6 col-lg-5">
          <div class="row g-2">
            <form-select
              class="col-7 form-input mb-3"
              label="Estado"
              v-model="stateOfBirthValue"
              :items="stateOptions"
              id="stateOfBirth"
              name="stateOfBirth"
            />
            <form-input
              v-if="!isIbgeAPIAvailable"
              class="col-5 form-input mb-3"
              label="Cidade de nascimento"
              v-model="cityOfBirthValue"
              id="cityOfBirth"
              name="cityOfBirth"
            />
            <form-select
              v-else
              :isDisabled="!stateOfBirthValue"
              class="col-5 form-input mb-3"
              label="Cidade de nascimento"
              v-model="cityOfBirthValue"
              :items="cityOptions"
              id="cityOfBirth"
              name="cityOfBirth"
            />
          </div>

          <form-input
            class="col-12 form-input mb-3"
            label="Profissão"
            v-model="professionValue"
            id="profession"
            name="profession"
          />

          <form-input
            class="col-12 form-input mb-3"
            label="Nome da empresa onde trabalha"
            v-model="pfCompanyNameValue"
            id="pfCompanyName"
            name="pfCompanyName"
          />

          <form-money
            className="col-12 form-input mb-3"
            v-model="averageMonthlyIncomeValue"
            label="Renda média mensal"
            id="averageMonthlyIncome"
            name="averageMonthlyIncome"
            :config="moneyFormat('R$ ', '.', ',', 2)"
          />

          <form-money
            className="col-12 form-input mb-3"
            v-model="totalAssetsValue"
            label="Valor do patrimônio em R$"
            id="totalAssets"
            name="totalAssets"
            :config="moneyFormat('R$ ', '.', ',', 2)"
          />

          <div class="col-12 mt-3">
            <p class="text-center">É ou possui parentesco com PEP?</p>
            <div class="radio-card-group">
              <label class="radio-card-label" :class="{ selected: isPepValue === false }">
                <input type="radio" :value="false" v-model="isPepValue" name="isPep" />
                <span class="custom-radio"></span>
                <span class="label-text">Não</span>
              </label>
              <label class="radio-card-label" :class="{ selected: isPepValue === true }">
                <input type="radio" :value="true" v-model="isPepValue" name="isPep" />
                <span class="custom-radio"></span>
                <span class="label-text">Sim</span>
              </label>
            </div>
          </div>
          <div v-if="alreadySubmittedOnce && errors.isPep" class="text-danger text-center error">
            {{ errors.isPep }}
          </div>

          <div class="row g-2" v-if="isPepValue === true">
            <form-input
              class="col-12 col-md-6 form-input mb-3"
              label="Nome completo"
              v-model="pepNameValue"
              id="pepName"
              name="pepName"
            />

            <form-input
              class="col-12 col-md-6 form-input mb-3"
              label="CPF"
              v-model="pepCpfValue"
              type="tel"
              id="pepCpf"
              name="pepCpf"
            />
          </div>

          <div class="col-12 mt-3">
            <p class="text-center">Possui outra cidadania?</p>

            <div class="radio-card-group">
              <label
                class="radio-card-label"
                :class="{ selected: hasOtherCitizenshipValue === false }"
              >
                <input
                  type="radio"
                  :value="false"
                  v-model="hasOtherCitizenshipValue"
                  name="hasOtherCitizenship"
                />
                <span class="custom-radio"></span>
                <span class="label-text">Não</span>
              </label>
              <label
                class="radio-card-label"
                :class="{ selected: hasOtherCitizenshipValue === true }"
              >
                <input
                  type="radio"
                  :value="true"
                  v-model="hasOtherCitizenshipValue"
                  name="hasOtherCitizenship"
                />
                <span class="custom-radio"></span>
                <span class="label-text">Sim</span>
              </label>
            </div>
          </div>
          <div
            v-if="alreadySubmittedOnce && errors.hasOtherCitizenship"
            class="text-danger text-center error"
          >
            {{ errors.hasOtherCitizenship }}
          </div>
          <form-input
            v-if="hasOtherCitizenshipValue === true"
            class="col-12 form-input mb-3"
            label="Qual cidadania?"
            v-model="otherCitizenshipValue"
            id="otherCitizenship"
            name="otherCitizenship"
          />

          <div class="col-12 mt-3">
            <p class="text-center">É cidadão ou residente fiscal dos EUA?</p>
            <div class="radio-card-group">
              <label
                class="radio-card-label"
                :class="{ selected: isEuaResidentialTaxCitizenValue === false }"
              >
                <input
                  type="radio"
                  :value="false"
                  v-model="isEuaResidentialTaxCitizenValue"
                  name="isEua"
                />
                <span class="custom-radio"></span>
                <span class="label-text">Não</span>
              </label>
              <label
                class="radio-card-label"
                :class="{ selected: isEuaResidentialTaxCitizenValue === true }"
              >
                <input
                  type="radio"
                  :value="true"
                  v-model="isEuaResidentialTaxCitizenValue"
                  name="isEua"
                />
                <span class="custom-radio"></span>
                <span class="label-text">Sim</span>
              </label>
            </div>
          </div>
          <div
            v-if="alreadySubmittedOnce && errors.isEuaResidentialTaxCitizen"
            class="text-danger text-center error"
          >
            {{ errors.isEuaResidentialTaxCitizen }}
          </div>

          <form-button
            class="col-12 form-input"
            title="Próximo"
            :loading="loading"
            :isDisabled="loading"
            @clicked="onSubmitAction"
          />
        </div>
      </div>
    </div>
  </RegisterLayout>
</template>

<style lang="scss" scoped></style>
